import React from 'react';
import { styled } from '@mui/material/styles';

const Page = styled('main')`
  position: relative;
  display: flex;
  min-height: 100vh;
  min-height: 100svh;
  overflow: hidden;
  flex-direction: column;
  isolation: isolate;
  background:
    radial-gradient(ellipse at 48% 45%, rgba(255, 255, 255, 0.94), transparent 52%),
    linear-gradient(116deg, #f8f9fb 0%, #f0f2f5 55%, #e5e8ec 100%);
  color: #111216;
  font-family: 'Poppins Regular', 'Poppins', Arial, sans-serif;

  * {
    box-sizing: border-box;
  }
`;

const Architecture = styled('svg')`
  position: absolute;
  z-index: -1;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0.94;

  @media (max-width: 700px) {
    top: 0%;
    left: 12%;
    width: 125%;
    height: 100%;
    opacity: 0.58;
  }
`;

const Header = styled('header')`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: clamp(26px, 3.2vw, 48px) clamp(28px, 6.2vw, 86px);
`;

const Wordmark = styled('div')`
  font-size: clamp(19px, 1.9vw, 25px);
  letter-spacing: -1.35px;
  white-space: nowrap;

  span:first-of-type {
    font-family: 'Poppins Bold', 'Poppins', Arial, sans-serif;
  }

  span:last-of-type {
    font-weight: 400;
  }
`;

const HeaderTagline = styled('div')`
  display: flex;
  align-items: center;
  gap: clamp(9px, 1.4vw, 19px);
  color: #25272b;
  font-size: clamp(8px, 0.72vw, 10px);
  font-weight: 500;
  letter-spacing: 0.2em;
  white-space: nowrap;

  span[aria-hidden='true'] {
    width: 17px;
    height: 1px;
    background-color: currentColor;
    opacity: 0.65;
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

const Copy = styled('section')`
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  width: min(100% - clamp(56px, 12.4vw, 172px), 1280px);
  margin: 0 auto;
  padding: 15px 0 65px;
  transform: translateY(-1.5%);

  @media (max-width: 700px) {
    justify-content: flex-start;
    padding-top: clamp(78px, 15vh, 135px);
    padding-bottom: 45px;
    transform: none;
  }
`;

const Eyebrow = styled('p')`
  margin: 0 0 21px;
  color: #55575c;
  font-size: clamp(10px, 0.9vw, 12px);
  font-weight: 500;
  letter-spacing: 0.34em;
`;

const Title = styled('h1')`
  max-width: 570px;
  margin: 0;
  font-size: clamp(42px, 4.65vw, 64px);
  font-weight: 400;
  letter-spacing: -0.065em;
  line-height: 1.06;

  @media (max-width: 700px) {
    max-width: 470px;
    font-size: clamp(42px, 10vw, 58px);
  }
`;

const Description = styled('p')`
  max-width: 430px;
  margin: 21px 0 0;
  color: #64666b;
  font-size: clamp(14px, 1.25vw, 17px);
  line-height: 1.6;
`;

const AccentLine = styled('div')`
  width: 36px;
  height: 1px;
  margin: 38px 0 39px;
  background-color: #7c7e82;
`;

const Contact = styled('p')`
  margin: 0;
  color: #62646a;
  font-size: 14px;

  a {
    color: #141519;
    font-family: 'Poppins SemiBold', 'Poppins', Arial, sans-serif;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const ArchitecturalArtwork = (): JSX.Element => {
  const ribs = Array.from({ length: 9 }, (_, index) => {
    const offset = index * 57;
    const innerTop = 590 + offset;
    const outerTop = 650 + offset;
    const innerBase = 365 + index * 50;
    const outerBase = 420 + index * 48;

    return (
      <path
        key={index}
        d={`M ${innerTop} -80
          C ${innerTop - 130} 110, ${innerBase - 135} 240, ${innerBase} 530
          C ${innerBase + 18} 580, ${innerBase + 40} 620, ${innerBase + 76} 651
          L ${outerBase + 48} 651
          C ${outerBase + 8} 606, ${outerBase - 6} 566, ${outerBase - 22} 523
          C ${outerBase - 130} 253, ${outerTop - 145} 102, ${outerTop} -80 Z`}
        fill="url(#ribShade)"
        stroke="rgba(255,255,255,0.9)"
        strokeWidth="5"
      />
    );
  });

  return (
    <Architecture
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
      role="presentation"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ribShade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#f5f6f8" />
          <stop offset="0.42" stopColor="#c8cbd0" />
          <stop offset="0.7" stopColor="#e8eaed" />
          <stop offset="1" stopColor="#aeb2b8" />
        </linearGradient>
        <linearGradient id="floorShade" x1="0" y1="0" x2="0.8" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor="#d9dce1" />
        </linearGradient>
        <linearGradient id="leftFade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#f7f8fa" stopOpacity="0.98" />
          <stop offset="0.48" stopColor="#f7f8fa" stopOpacity="0.88" />
          <stop offset="1" stopColor="#f7f8fa" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M 600 0 H 1440 V 900 H 475 C 650 780 730 635 780 435 C 835 215 795 100 600 0 Z" fill="#e7e9ed" />
      <g>{ribs}</g>
      <path
        d="M 0 795 C 250 770 420 812 650 748 C 835 696 975 729 1440 833 V 900 H 0 Z"
        fill="url(#floorShade)"
      />
      <path
        d="M 0 798 C 250 773 420 815 650 751 C 835 699 975 732 1440 836"
        fill="none"
        stroke="rgba(255,255,255,0.95)"
        strokeWidth="15"
      />
      <path d="M 0 0 H 700 V 900 H 0 Z" fill="url(#leftFade)" />
      <path
        d="M 0 790 C 205 790 376 810 565 772"
        fill="none"
        stroke="rgba(255,255,255,0.8)"
        strokeWidth="2"
      />
    </Architecture>
  );
};

const ComingSoon = (): JSX.Element => (
  <Page>
    <ArchitecturalArtwork />
    <Header>
      <Wordmark aria-label="CoreEstate.io">
        <span>core</span>
        <span>estate.io</span>
      </Wordmark>
      <HeaderTagline>
        <span>REAL ESTATE</span>
        <span aria-hidden="true" />
        <span>TECHNOLOGY</span>
        <span aria-hidden="true" />
        <span>A BRIGHTER TOMORROW</span>
      </HeaderTagline>
    </Header>
    <Copy>
      <Eyebrow>A NEW CHAPTER AHEAD</Eyebrow>
      <Title>
        A Better
        <br />
        CoreEstate.io
        <br />
        Is on the Way.
      </Title>
      <Description>
        We’re rebuilding to bring you a more powerful, modern, and impactful
        experience.
      </Description>
      <AccentLine />
      <Contact>
        For business inquiries:{' '}
        <a href="mailto:hello@coreestate.io">hello@coreestate.io</a>
      </Contact>
    </Copy>
  </Page>
);

export default ComingSoon;
